import React, {useEffect} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  SxProps,
  Typography
} from '@mui/material';
import {t} from 'ttag';
import {InvoiceDetails, TicketHeader, InvoiceEditDialog, SendInvoiceDialog, styles} from '@invoice-system';
import {EditNote, PrintOutlined, SendOutlined} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useDownloadInvoiceMutation, useGetInvoicesQuery} from '../../store/apis/ccs';
import {useParams} from 'react-router';
import RTKQueryFeedback from '@components/common/RTKQueryFeedback';
import {useSnackbar} from 'notistack';
import {InvoicePreview} from '@invoice-system/components/InvoicePreview';

export const InvoiceSystemView = () => {
  const {documentId} = useParams();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const [dialog, setDialog] = React.useState<'send' | 'edit' | 'new' | null>(null);
  const {data, isFetching} = useGetInvoicesQuery({documentId: documentId as string}, {skip: !documentId});
  const notValidPaymentCodes = data?.[0]?.details?.Payment?.map((p) => {
    if (p.Code === 'MS' || p.Code === 'GR') return p;
  });
  const [downloadPdf, {isLoading: loadingPdf}] = useDownloadInvoiceMutation();

  useEffect(() => {
    if (!isFetching && !data?.[0]?.details?.PNR && documentId) {
      enqueueSnackbar(!data ? 'SAP Service is temporarily unavailable' :t`Ticket not found with ticket number` + ` ${documentId}`, {variant: 'error'});
      navigate('/invoice/search');
    }
  }, [data?.[0]?.details?.PNR, isFetching]);

  useEffect(() => {
    if (data && !data?.[0]?.docId) {
      setDialog('new');
    }
  }, [data?.[0]?.docId]);

  if (isFetching || !data?.[0]?.details?.PNR) {
    return <RTKQueryFeedback isLoading={true} />;
  }

  return (
    <Card elevation={3}>
      <CardHeader title={<Typography sx={styles.title}>{t`Invoice System`}</Typography>} />
      <Divider />
      {notValidPaymentCodes?.find((p) => p?.Code) && (
        <Alert sx={{borderRadius: '0px 0px 4px 4px'}} variant={'standard'} severity={'error'}>
          {t`This ticket has a payment(s) with the code(s)` + ' ' +
            notValidPaymentCodes
              ?.filter((p) => p?.Code)
              ?.map((p) => p?.Code)
              .join(', ') + ' ' + t`and these payments are not editable in the Invoice System.`}
        </Alert>
      )}
      <CardContent>
        <Typography sx={styles.subtitle} mb={2}>{t`Ticket Details`}</Typography>
        <TicketHeader ticketId={documentId} data={data?.[0]} />
        <Typography sx={styles.subtitle} my={2}>{t`Invoice Details`}</Typography>
        <InvoiceDetails data={data?.[0]} />
        <InvoicePreview data={data?.[0]} downloadPdf={downloadPdf} />
      </CardContent>
      <Divider />
      <CardActions sx={styles.cardActions as SxProps}>
        <Button variant={'outlined'} sx={styles.returnButton} color={'primary'} onClick={() => navigate(-1)}>
          {t`Return`}
        </Button>
        <Box display={'flex'} sx={styles.buttonsBox}>
          <Button
            variant={'outlined'}
            color={'secondary'}
            startIcon={<PrintOutlined />}
            onClick={() => {
                const a = document.getElementById('preview-document') as HTMLIFrameElement;
                if (a?.contentWindow) {
                  a?.focus();
                  a?.contentWindow?.print();
                }
              }}
            disabled={loadingPdf || (data && !data?.[0]?.docId)}
          >
            {t`Print`}
          </Button>
          <Button
            variant={'contained'}
            color={'primary'}
            startIcon={<SendOutlined sx={{rotate: '-40deg', mb: 0.5}} />}
            disabled={data && !data?.[0]?.docId}
            onClick={() => setDialog('send')}
          >
            {t`Send Options`}
          </Button>
          <Button
            variant={'contained'}
            color={'warning'}
            startIcon={<EditNote sx={{fontSize: '23px !important'}} />}
            onClick={() => setDialog('edit')}
            disabled={Number(data?.[0]?.sequence) >= 2 || Boolean(notValidPaymentCodes?.find((p) => p?.Code))}
          >
            {t`Edit Invoice`}
          </Button>
        </Box>
      </CardActions>
      {dialog === 'edit' && <InvoiceEditDialog onClose={() => setDialog(null)} data={data?.[0]} />}
      {dialog === 'send' && <SendInvoiceDialog onClose={() => setDialog(null)} data={data?.[0]} />}
      {(dialog === 'new' && !notValidPaymentCodes?.find((p) => p?.Code)) && <InvoiceEditDialog onClose={() => setDialog(null)} data={data?.[0]}/> }
    </Card>
  );
};
